import { useMemo, useState } from 'react';

import AddContentDialog from './AddContentDialog';
import AisleContentHeader from './AisleContentHeader';
import AisleContentNav from './AisleContentNav';
import AisleContentSection, { AisleContentSectionSkeleton } from './AisleContentSection';
import { CONTENT_VARIANTS } from './constants';
import { buildAisleTree, flattenAisleTree, groupBannersByAisle, groupCarouselsByAisle } from './utils';
import { useActiveAisleObserver } from './hooks/useActiveAisleObserver';
import { useAisleContentQueryParams } from './hooks/useAisleContentQueryParams';
import { useAisles } from './hooks/useAisles';
import { useBanners } from './hooks/useBanners';
import { useCarousels } from './hooks/useCarousels';
import { useDateSelection } from './hooks/useDateSelection';
import { usePackGroupSelection } from './hooks/usePackGroupSelection';
import { useSellableItems } from './hooks/useSellableItems';

/**
 * Displays a list of aisles and their configurable content (banners, carousels).
 */
export default function AisleContent() {
    const { queryParamsData, updateQueryParams } = useAisleContentQueryParams();
    const dateSelection = useDateSelection(queryParamsData.date ?? new Date(), updateQueryParams);
    const packGroupSelection = usePackGroupSelection(
        dateSelection.date,
        queryParamsData.packGroupID,
        updateQueryParams,
    );

    const { aisles, loading: aislesLoading } = useAisles();
    const { banners, loading: bannersLoading, refetch: refetchBanners } = useBanners();

    const {
        carousels,
        loading: carouselsLoading,
        refetch: refetchCarousels,
    } = useCarousels(queryParamsData.packGroupID, 'aisle');
    const { sellableItems } = useSellableItems(queryParamsData.packGroupID);

    const aislesTree = useMemo(() => buildAisleTree(aisles), [aisles]);
    const sortedAisles = useMemo(() => flattenAisleTree(aislesTree), [aislesTree]);
    const bannersByAisle = useMemo(() => groupBannersByAisle(banners), [banners]);
    const carouselsByAisle = useMemo(() => groupCarouselsByAisle(carousels), [carousels]);

    const aisleContentLoading = bannersLoading || carouselsLoading;

    const { activeAisleID, aisleRefs } = useActiveAisleObserver(sortedAisles, aisleContentLoading);

    const [dialogAisleID, setDialogAisleID] = useState(null);
    const [newCarouselEditors, setNewCarouselEditors] = useState({});
    const [newBannerEditors, setNewBannerEditors] = useState({});

    const handleAddNewEditor = ({ aisleID, variantID }) => {
        const variant = CONTENT_VARIANTS[variantID];
        const editorType = variant?.category; // 'carousel' | 'banner'
        const setNewEditors = editorType === 'carousel' ? setNewCarouselEditors : setNewBannerEditors;

        if (!variant || !setNewEditors) return;

        setNewEditors((prev) => ({
            ...prev,
            [aisleID]: [
                {
                    id: crypto.randomUUID(),
                    aisleID,
                    variant,
                },
                ...(prev[aisleID] || []),
            ],
        }));
    };

    const handleDeleteNewEditor = ({ aisleID, editorIndex, editorType }) => {
        const setNewEditors = editorType === 'carousel' ? setNewCarouselEditors : setNewBannerEditors;
        if (!setNewEditors) return;

        setNewEditors((prev) => {
            const newEditors = prev[aisleID]?.filter((_, index) => index !== editorIndex) || [];
            return { ...prev, [aisleID]: newEditors };
        });
    };

    return (
        <>
            <AisleContentHeader
                {...dateSelection}
                {...packGroupSelection}
                aisles={sortedAisles}
                carousels={carousels}
                refetchCarousels={refetchCarousels}
            />
            <section className="flex gap-10 w-full max-w-7xl mx-auto pt-8 pb-10">
                <AisleContentNav activeAisleID={activeAisleID} aisleTree={aislesTree} loading={aislesLoading} />
                <ol className="flex flex-col items-start gap-12 w-full">
                    {!aisleContentLoading &&
                        sortedAisles.map((aisle) => (
                            <AisleContentSection
                                key={aisle.id}
                                aisle={aisle}
                                aisleRefs={aisleRefs}
                                banners={bannersByAisle[aisle.id] ?? []}
                                carousels={carouselsByAisle[aisle.id] ?? []}
                                handleDeleteNewEditor={handleDeleteNewEditor}
                                newBannerEditors={newBannerEditors[aisle.id] ?? []}
                                newCarouselEditors={newCarouselEditors[aisle.id] ?? []}
                                refetchBanners={() => refetchBanners(true)}
                                refetchCarousels={() => refetchCarousels(true)}
                                sellableItems={sellableItems}
                                setDialogAisleID={setDialogAisleID}
                                sortedAisles={sortedAisles}
                            />
                        ))}
                    {aisleContentLoading && <AisleContentSectionSkeleton />}
                </ol>
            </section>

            <AddContentDialog
                dialogAisleID={dialogAisleID}
                existingBanner={bannersByAisle[dialogAisleID]}
                existingCarousel={carouselsByAisle[dialogAisleID]}
                handleAddNewEditor={handleAddNewEditor}
                handleClose={() => setDialogAisleID(null)}
                newBannerEditors={dialogAisleID ? (newBannerEditors[dialogAisleID] ?? []) : []}
                newCarouselEditors={dialogAisleID ? (newCarouselEditors[dialogAisleID] ?? []) : []}
            />
        </>
    );
}
